import { LogErrors } from './../../LogErrors'

import {
    GetAssignedLicensesForInstructor as DLGetAssignedLicensesForInstructor,
    GetTotalUsedLicenses as DLGetTotalUsedLicenses,
    GetUsedLicensesReport as DLGetUsedLicensesReport,
    GetCenterFreeLicensesStatus as DLGetCenterFreeLicensesStatus
} from './../../DL/Instructor/Licenses'

/**
 * Función auxiliar para obtener datos base de sesión de forma segura.
 */
const getSessionBaseData = () => {
    if (!sessionStorage.getItem('userData')) return null

    const userData = JSON.parse(sessionStorage.getItem('userData'))
    const token = userData?.token
    const idCenter = userData?.navigation?.center?.id

    if (!token || !idCenter) return null
    return { token, idCenter }
}

/**
 * Resumen de licencias del propio instructor: asignadas, utilizadas y restantes
 * (restantes = asignadas - utilizadas, no viene de ningún endpoint).
 */
const GetLicensesSummary = async () => {
    try {
        const session = getSessionBaseData()
        if (!session) return { success: false, message: 'Sesión expirada o inválida.' }

        const [resAssigned, resUsed] = await Promise.all([
            DLGetAssignedLicensesForInstructor(session.idCenter, session.token),
            DLGetTotalUsedLicenses(session.idCenter, session.token)
        ])

        if (!resAssigned.success) await LogErrors('GetLicensesSummary (Assigned) - BL Instructor Licenses', resAssigned.message)
        if (!resUsed.success) await LogErrors('GetLicensesSummary (Used) - BL Instructor Licenses', resUsed.message)

        const assigned = resAssigned.success ? (resAssigned.data?.instructors?.[0]?.totalLicenses ?? 0) : 0
        const used = resUsed.success ? (resUsed.data?.licensesUsed ?? 0) : 0

        return {
            success: true,
            data: {
                assigned,
                used,
                remaining: assigned - used
            }
        }
    } catch (error) {
        await LogErrors('GetLicensesSummary - BL Instructor Licenses', error)
        return { success: false, message: 'Error interno al procesar el resumen de licencias.' }
    }
}

/**
 * Catálogo de cursos para el desplegable de filtro de licencias utilizadas. Se obtiene
 * de GetAssignedLicensesForInstructor (mismo endpoint del resumen) en lugar de un
 * endpoint de filtros dedicado, ya que ese ya trae los cursos con licencia del instructor.
 */
const GetCoursesForFilter = async () => {
    try {
        const session = getSessionBaseData()
        if (!session) return { success: false, message: 'Sesión expirada o inválida.' }

        const response = await DLGetAssignedLicensesForInstructor(session.idCenter, session.token)
        if (!response.success) return { success: false, message: response.message }

        const courses = response.data?.instructors?.[0]?.courses || []
        const mapped = courses.map(c => ({ idCourse: c.id, courseName: c.coursename, level: c.level }))

        return { success: true, data: mapped }
    } catch (error) {
        await LogErrors('GetCoursesForFilter - BL Instructor Licenses', error)
        return { success: false, message: 'Error interno al obtener los cursos disponibles.' }
    }
}

/**
 * Reporte de licencias utilizadas por los propios Ztudiantes del instructor. La API
 * envuelve el listado en la propiedad "usedLicenses" y lo agrupa por instructor > curso >
 * estudiante > grupos; aquí se aplana a un arreglo de filas para la tabla/exportación.
 */
const GetUsedLicensesReport = async (idCourse = null) => {
    try {
        const session = getSessionBaseData()
        if (!session) return { success: false, message: 'Sesión expirada o inválida.' }

        const response = await DLGetUsedLicensesReport(session.idCenter, session.token, idCourse)
        if (!response.success) return { success: false, message: response.message }

        const usedLicenses = response.data?.usedLicenses || []
        const flatRows = []

        usedLicenses.forEach(instructor => {
            if (!Array.isArray(instructor.courses)) return

            instructor.courses.forEach(course => {
                const courseName = course.levelName
                    ? `${course.courseName || ''} (Nivel ${course.levelName})`
                    : (course.courseName || '')
                if (!Array.isArray(course.students)) return

                course.students.forEach(student => {
                    // Un estudiante puede pertenecer a más de un grupo
                    const groupNames = Array.isArray(student.groups)
                        ? student.groups.map(g => g.groupName || '').filter(name => name !== '').join(', ')
                        : ''

                    flatRows.push({
                        idCourse: course.idCourse,
                        courseName,
                        idStudent: student.idStudent,
                        studentName: student.studentName || '',
                        user: student.user || '',
                        groupName: groupNames,
                        registrationDate: student.registrationDate || ''
                    })
                })
            })
        })

        return { success: true, data: flatRows }
    } catch (error) {
        await LogErrors('GetUsedLicensesReport - BL Instructor Licenses', error)
        return { success: false, message: 'Error interno al obtener el reporte de licencias utilizadas.' }
    }
}

/**
 * Consulta si el centro está en modalidad de licencias gratuitas, para decidir si el tile
 * "Licencias Ztudia" del dashboard del Instructor debe habilitarse o permanecer deshabilitado.
 */
const GetCenterFreeLicensesStatus = async () => {
    try {
        const session = getSessionBaseData()
        if (!session) return { success: false, message: 'Sesión expirada o inválida.' }

        const idUserRol = 2 // Perfil de Instructor (ROLES.INSTRUCTOR)

        const response = await DLGetCenterFreeLicensesStatus(session.idCenter, idUserRol, session.token)
        return response
    } catch (error) {
        await LogErrors('GetCenterFreeLicensesStatus - BL Instructor Licenses', error)
        return { success: false, message: 'Error interno al consultar el estado de licencias gratuitas del centro.' }
    }
}

export { GetLicensesSummary, GetCoursesForFilter, GetUsedLicensesReport, GetCenterFreeLicensesStatus };
